import React, { useState, useRef, useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useAppState } from '../context/AppState'
import { slotToTime, TIMEBOX_TYPES, FIRST_SLOT, LAST_SLOT, TIME_AREAS } from '../data/seed'
import { TimeboxBlock } from './TimeboxBlock'
import { BlockListWatch } from './BlockListWatch'

function DragCaptureOverlay({ dragId, onMove, onEnd }) {
  const elRef = useRef(null)
  const dragIdRef = useRef(dragId)
  dragIdRef.current = dragId

  useEffect(() => {
    const el = elRef.current
    if (!el) return
    const touchMove = e => {
      e.preventDefault()
      const t = e.changedTouches[0] || e.touches[0]
      if (t) onMove(dragIdRef.current, t.clientX, t.clientY)
    }
    const touchEnd = e => {
      e.preventDefault()
      onEnd()
    }
    el.addEventListener('touchmove', touchMove, { passive: false })
    el.addEventListener('touchend', touchEnd, { passive: false })
    el.addEventListener('touchcancel', touchEnd, { passive: false })
    return () => {
      el.removeEventListener('touchmove', touchMove)
      el.removeEventListener('touchend', touchEnd)
      el.removeEventListener('touchcancel', touchEnd)
    }
  }, [onMove, onEnd])

  return (
    <div
      ref={elRef}
      className="timebox-drag-capture"
      aria-hidden
      onPointerMove={e => {
        e.preventDefault()
        e.stopPropagation()
        onMove(dragId, e.clientX, e.clientY)
      }}
      onPointerUp={e => {
        e.stopPropagation()
        onEnd()
      }}
      onPointerCancel={e => {
        e.stopPropagation()
        onEnd()
      }}
    />
  )
}

const SLOT_HEIGHT_DEFAULT = 42   /* ~12% smaller than 48 */
const SLOT_HEIGHT_WATCH = 28
const SLOT_HEIGHT_TINY = 22   /* 1.73" Wear OS */
const BLOCK_GAP_DEFAULT = 5.5   /* gap between blocks (~10% more than 5); 0 for watch/tiny */
const MOVE_THROTTLE_MS = 50
const RULER_WIDTH_PX = 56   /* matches .timebox-column { left: 56px } */

const MERGE_BOUNDARY_VISIBLE_MS = 3000

export function DayView({ day, isWatchView = false, isTinyWatch = false }) {
  const { state, addTimebox, moveTimebox, setDayLocked, splitTimebox, mergeTimeboxes } = useAppState()
  const timeboxes = state.days[day]?.timeboxes ?? []
  const locked = state.days[day]?.locked ?? false
  const types = state.timeboxTypes ?? TIMEBOX_TYPES
  const [adding, setAdding] = useState(false)
  const [showMergeBoundaries, setShowMergeBoundaries] = useState(false)
  const mergeBoundaryTimeoutRef = useRef(null)
  const SLOT_HEIGHT = isTinyWatch ? SLOT_HEIGHT_TINY : isWatchView ? SLOT_HEIGHT_WATCH : SLOT_HEIGHT_DEFAULT
  const BLOCK_GAP = isTinyWatch || isWatchView ? 0 : BLOCK_GAP_DEFAULT
  const ROW_HEIGHT = SLOT_HEIGHT + BLOCK_GAP
  const TOTAL_HEIGHT = ROW_HEIGHT * (LAST_SLOT - FIRST_SLOT + 1)
  const computeLayout = useCallback((list) => {
    let y = 0
    return list.map(tb => {
      const len = tb.lengthSlots ?? 1
      const height = len * SLOT_HEIGHT
      const top = y
      y += height + BLOCK_GAP
      return { top, height }
    })
  }, [SLOT_HEIGHT, BLOCK_GAP])

  const blockLayout = React.useMemo(() => computeLayout(timeboxes), [timeboxes, computeLayout])
  const contentHeight = blockLayout.length > 0
    ? blockLayout[blockLayout.length - 1].top + blockLayout[blockLayout.length - 1].height
    : TOTAL_HEIGHT
  const columnHeight = BLOCK_GAP > 0 ? Math.max(TOTAL_HEIGHT, contentHeight) : TOTAL_HEIGHT
  const [dragId, setDragId] = useState(null)
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 })
  const [dragOriginY, setDragOriginY] = useState(0)
  const [dragHasMoved, setDragHasMoved] = useState(false)
  /** Gap position when drag started, so the empty space stays fixed instead of following the drop target */
  const [dragOriginGap, setDragOriginGap] = useState(null)
  const columnRef = useRef(null)
  const lastMoveRef = useRef(0)
  const rafRef = useRef(null)
  const dragStateRef = useRef({ id: null, originY: 0, position: { x: 0, y: 0 }, hasMoved: false, lastClamped: null })
  const endedRef = useRef(false)

  const handleColumnPointerEnter = useCallback(() => {
    if (mergeBoundaryTimeoutRef.current) {
      clearTimeout(mergeBoundaryTimeoutRef.current)
      mergeBoundaryTimeoutRef.current = null
    }
    if (!locked) setShowMergeBoundaries(true)
    mergeBoundaryTimeoutRef.current = setTimeout(() => {
      setShowMergeBoundaries(false)
      mergeBoundaryTimeoutRef.current = null
    }, MERGE_BOUNDARY_VISIBLE_MS)
  }, [locked])

  const handleColumnPointerLeave = useCallback(() => {
    if (mergeBoundaryTimeoutRef.current) {
      clearTimeout(mergeBoundaryTimeoutRef.current)
      mergeBoundaryTimeoutRef.current = null
    }
    setShowMergeBoundaries(false)
  }, [])

  useEffect(() => {
    return () => {
      if (mergeBoundaryTimeoutRef.current) clearTimeout(mergeBoundaryTimeoutRef.current)
    }
  }, [])

  const handleDragStart = useCallback((timeboxId, clientX, clientY) => {
    endedRef.current = false
    setDragId(timeboxId)
    setDragPosition({ x: clientX, y: clientY })
    setDragOriginY(clientY)
    setDragHasMoved(false)
    setDragOriginGap(null)
    lastMoveRef.current = 0
    dragStateRef.current = { id: timeboxId, originY: clientY, position: { x: clientX, y: clientY }, hasMoved: false, lastClamped: null }
    const columnEl = columnRef.current?.querySelector('.timebox-column')
    const blockEl = columnEl?.querySelector(`[data-timebox-id="${timeboxId}"]`)
    if (columnEl && blockEl) {
      const colRect = columnEl.getBoundingClientRect()
      const blockRect = blockEl.getBoundingClientRect()
      const top = blockRect.top - colRect.top
      const height = blockRect.height
      setDragOriginGap({ top, height })
    } else {
      const idx = timeboxes.findIndex(t => t.id === timeboxId)
      if (idx >= 0 && blockLayout[idx]) setDragOriginGap({ top: blockLayout[idx].top, height: blockLayout[idx].height })
    }
  }, [timeboxes, blockLayout])

  const processMove = useCallback((timeboxId, clientX, clientY) => {
    const ds = dragStateRef.current
    if (!columnRef.current) return
    const rect = columnRef.current.getBoundingClientRect()
    const block = timeboxes.find(t => t.id === timeboxId)
    const len = block?.lengthSlots ?? 1
    const y = Math.max(0, Math.min(contentHeight - 1, clientY - rect.top))
    const numSlots = LAST_SLOT - FIRST_SLOT + 1
    const slot = BLOCK_GAP > 0 && contentHeight > 0
      ? FIRST_SLOT + Math.floor((y / contentHeight) * numSlots)
      : Math.floor(y / ROW_HEIGHT) + FIRST_SLOT
    const maxStart = LAST_SLOT - len + 1
    const clamped = Math.max(FIRST_SLOT, Math.min(maxStart, slot))

    const columnCenterX = rect.left + RULER_WIDTH_PX + (rect.width - RULER_WIDTH_PX) / 2

    const deltaY = Math.abs(clientY - ds.originY)
    const thresholdPx = ROW_HEIGHT * 0.25
    if (!ds.hasMoved && deltaY < thresholdPx) return
    if (!ds.hasMoved) {
      ds.hasMoved = true
      setDragHasMoved(true)
      ds.lastClamped = clamped
      if (dragOriginGap) {
        ds.position = {
          x: columnCenterX,
          y: (rect.top + dragOriginGap.top + (dragOriginGap.height / 2))+10,
        }
      }
    } else {
      ds.lastClamped = clamped
      ds.position = {
        x: columnCenterX,
        y: clientY,
      }
    }
    const now = performance.now()
    if (now - lastMoveRef.current >= MOVE_THROTTLE_MS) {
      lastMoveRef.current = now
      moveTimebox(day, timeboxId, clamped)
    }
  }, [day, timeboxes, dragOriginGap, moveTimebox, ROW_HEIGHT, BLOCK_GAP, contentHeight])

  const handleDragMove = useCallback((timeboxId, clientX, clientY) => {
    processMove(timeboxId, clientX, clientY)
    if (rafRef.current == null) {
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null
        setDragPosition(prev => ({ ...dragStateRef.current.position }))
      })
    }
  }, [processMove])

  const handleDragEnd = useCallback(() => {
    if (endedRef.current) return
    endedRef.current = true
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
    const ds = dragStateRef.current
    const blockId = ds.id
    const finalSlot =
      blockId && ds.hasMoved && columnRef.current && ds.lastClamped != null
        ? (() => {
            const block = timeboxes.find(t => t.id === blockId)
            if (!block) return null
            const len = block.lengthSlots ?? 1
            const maxStart = LAST_SLOT - len + 1
            return Math.max(FIRST_SLOT, Math.min(maxStart, ds.lastClamped))
          })()
        : null

    dragStateRef.current = { id: null, originY: 0, position: { x: 0, y: 0 }, hasMoved: false, lastClamped: null }
    setDragId(null)
    setDragOriginGap(null)

    // Defer final move so block becomes visible at current position first, then animates to drop slot
    if (finalSlot != null && blockId) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => moveTimebox(day, blockId, finalSlot))
      })
    }
  }, [day, timeboxes, moveTimebox, contentHeight, BLOCK_GAP, ROW_HEIGHT])

  const draggedBlock = dragId ? timeboxes.find(tb => tb.id === dragId) : null
  const draggedType = draggedBlock ? types.find(ty => ty.id === draggedBlock.typeId) : null

  /* Wear OS: vista lista blocchi invece della timeline */
  if (isWatchView) {
    return (
      <div className="day-view day-view-watch">
        <BlockListWatch timeboxes={timeboxes} types={types} />
      </div>
    )
  }

  return (
    <div className="day-view">
      <div className="day-mode-bar" aria-live="polite">
        <span className={`day-mode-badge ${locked ? 'day-mode-execution' : 'day-mode-planning'}`}>
          {locked ? 'Execution' : 'Planning'}
        </span>
        {locked ? (
          <button
            type="button"
            className="edit-plan-btn"
            onClick={() => setDayLocked(day, false)}
            aria-label="Edit plan"
          >
            Edit plan
          </button>
        ) : (
          <button
            type="button"
            className="confirm-plan-btn"
            onClick={() => setDayLocked(day, true)}
            aria-label="Confirm plan"
          >
            Confirm plan
          </button>
        )}
      </div>
      <div className="day-timeline" ref={columnRef} style={{ minHeight: columnHeight }}>
        <div className="time-ruler" style={{ height: columnHeight }}>
          {Array.from({ length: LAST_SLOT - FIRST_SLOT + 1 }, (_, i) => FIRST_SLOT + i).map(
            slot => (
              <div
                key={slot}
                className="time-slot-line"
                style={{ top: (slot - FIRST_SLOT) * ROW_HEIGHT }}
              >
                <span className="time-label">{slotToTime(slot)}</span>
              </div>
            )
          )}
        </div>

        <div
          className={`timebox-column timebox-column-animated ${showMergeBoundaries ? 'timebox-column-merge-visible' : ''}`}
          style={{ height: columnHeight }}
          onPointerEnter={!isWatchView && !locked ? handleColumnPointerEnter : undefined}
          onPointerLeave={!isWatchView && !locked ? handleColumnPointerLeave : undefined}
        >
          {/* Merge boundaries between adjacent same-type blocks */}
          {!isWatchView && !locked && showMergeBoundaries && timeboxes.map((tb, i) => {
            const next = timeboxes[i + 1]
            if (!next) return null
            const curEnd = tb.startSlot + (tb.lengthSlots ?? 1)
            if (tb.typeId !== next.typeId || curEnd !== next.startSlot) return null
            const topPx = blockLayout[i] ? blockLayout[i].top + blockLayout[i].height : (curEnd - FIRST_SLOT) * ROW_HEIGHT
            return (
              <div
                key={`merge-${tb.id}-${next.id}`}
                className="timebox-merge-boundary"
                style={{ top: topPx }}
              >
                <button
                  type="button"
                  className="timebox-merge-btn"
                  aria-label="Merge blocks"
                  onClick={() => mergeTimeboxes(day, tb.id, next.id)}
                  onPointerDown={e => { e.stopPropagation() }}
                />
              </div>
            )
          })}
          {/* Simple separators between Morning / Lunch / Afternoon for visual clarity */}
          {TIME_AREAS.slice(1).map(area => {
            const top = (area.startSlot - FIRST_SLOT) * ROW_HEIGHT
            return (
              <div
                key={area.id}
                className="time-separator"
                style={{ top }}
                aria-hidden
              >
                <span className="time-separator-label">{area.label}</span>
              </div>
            )
          })}

          {timeboxes.map((tb, i) => {
            const type = types.find(ty => ty.id === tb.typeId) ?? { label: tb.typeId, color: '#444' }
            const layout = blockLayout[i] ?? { top: (tb.startSlot - FIRST_SLOT) * ROW_HEIGHT, height: (tb.lengthSlots ?? 1) * SLOT_HEIGHT }
            const top = layout.top
            const height = layout.height
            const isLifted = dragId === tb.id && dragHasMoved
            return (
              <React.Fragment key={tb.id}>
                {isLifted && dragOriginGap && (
                  <div
                    className="timebox-gap"
                    style={{ top: `${dragOriginGap.top}px`, height: `${dragOriginGap.height}px` }}
                    aria-hidden
                  />
                )}
                <TimeboxBlock
                  id={tb.id}
                  label={type.label}
                  color={type.color}
                  typeId={tb.typeId}
                  lengthSlots={tb.lengthSlots ?? 1}
                  startSlot={tb.startSlot}
                  top={isLifted && dragOriginGap ? dragOriginGap.top : top}
                  height={isLifted && dragOriginGap ? dragOriginGap.height : height}
                  isDragging={false}
                  isLifted={false}
                  locked={locked}
                  onMoveUp={!isWatchView && !locked ? () => {
                    const newSlot = Math.max(FIRST_SLOT, tb.startSlot - 1)
                    if (newSlot !== tb.startSlot) moveTimebox(day, tb.id, newSlot)
                  } : undefined}
                  onMoveDown={!isWatchView && !locked ? () => {
                    const len = tb.lengthSlots ?? 1
                    const maxStart = LAST_SLOT - len + 1
                    const newSlot = Math.min(maxStart, tb.startSlot + 1)
                    if (newSlot !== tb.startSlot) moveTimebox(day, tb.id, newSlot)
                  } : undefined}
                  onSplit={!isWatchView && !locked ? splitAfterSlot => splitTimebox(day, tb.id, splitAfterSlot) : undefined}
                />
              </React.Fragment>
            )
          })}
        </div>
      </div>

      {!isWatchView && dragId && typeof document !== 'undefined' && document.body &&
        createPortal(
          <DragCaptureOverlay
            dragId={dragId}
            onMove={handleDragMove}
            onEnd={handleDragEnd}
          />,
          document.body
        )}

      {!isWatchView && dragId && dragHasMoved && draggedBlock && draggedType && typeof document !== 'undefined' && document.body &&
        createPortal(
          <div
            className="timebox-drag-preview"
            style={{
              '--block-color': draggedType.color,
              left: dragPosition.x,
              top: dragPosition.y,
              height: (draggedBlock.lengthSlots ?? 1) * SLOT_HEIGHT,
            }}
          >
            <div className="timebox-block-inner">
              <div className="timebox-block-label-wrap">
                <span className="timebox-label">{draggedType.label}</span>
                {(draggedBlock.lengthSlots ?? 1) > 1 && (
                  <span className="timebox-duration">{draggedBlock.lengthSlots ?? 1} timeboxes</span>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}

      {!isWatchView && !locked && adding ? (
        <div className="add-timebox-picker">
          <p>Add timebox</p>
          <div className="type-grid">
            {types.map(t => (
              <button
                key={t.id}
                type="button"
                className="type-chip"
                style={{ borderColor: t.color }}
                onClick={() => {
                  addTimebox(day, t.id)
                  setAdding(false)
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
          <button type="button" className="cancel-add" onClick={() => setAdding(false)}>
            Cancel
          </button>
        </div>
      ) : !isWatchView && !locked ? (
        <button type="button" className="add-timebox-btn" onClick={() => setAdding(true)}>
          + Add timebox
        </button>
      ) : null}
    </div>
  )
}
